/**
 * Add-on illustrations — a larger, monochrome line drawing per add-on, shown
 * centered in the purchase-confirmation dialog (distinct from the small lucide
 * icon in the row). Mono by design: they stroke `currentColor`, so the caller's
 * muted/gray text colour tints them and they auto-flip in dark mode. One subject
 * per add-on so they read as decoration, not status.
 */
import * as React from "react";

type AddonIllustrationProps = { addonKey: string; size?: number };

const SVG_PROPS = {
  viewBox: "0 0 96 96",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
} as const;

/** Flint AI — a cluster of sparkles (matches the AI = spark motif). */
function AiArt({ size }: { size: number }) {
  return (
    <svg width={size} height={size} {...SVG_PROPS}>
      <path d="M48 16l5.5 17 17 5.5-17 5.5-5.5 17-5.5-17-17-5.5 17-5.5z" />
      <path d="M75 50l2.6 7.4 7.4 2.6-7.4 2.6-2.6 7.4-2.6-7.4-7.4-2.6 7.4-2.6z" />
      <path d="M24 28l1.9 5.6 5.6 1.9-5.6 1.9-1.9 5.6-1.9-5.6-5.6-1.9 5.6-1.9z" />
    </svg>
  );
}

/** Garment OS — a t-shirt. */
function GarmentArt({ size }: { size: number }) {
  return (
    <svg width={size} height={size} {...SVG_PROPS}>
      <path d="M37 20l-17 9 7 14 7-4v31h36V39l7 4 7-14-17-9a11 11 0 0 1-22 0z" />
    </svg>
  );
}

/** Point of Sale — a shopfront with an awning. */
function PosArt({ size }: { size: number }) {
  return (
    <svg width={size} height={size} {...SVG_PROPS}>
      <path d="M20 43v30h56V43" />
      <path d="M16 43l6-16h52l6 16z" />
      <path d="M34 27l-2 16M50 27v16M66 27l2 16" />
      <path d="M42 73V57h12v16" />
    </svg>
  );
}

/** WhatsApp Business — a chat bubble with typing dots. */
function WhatsappArt({ size }: { size: number }) {
  return (
    <svg width={size} height={size} {...SVG_PROPS}>
      <path d="M16 32a8 8 0 0 1 8-8h48a8 8 0 0 1 8 8v22a8 8 0 0 1-8 8H40L26 74V62h-2a8 8 0 0 1-8-8z" />
      <circle cx="34" cy="43" r="2.6" fill="currentColor" stroke="none" />
      <circle cx="48" cy="43" r="2.6" fill="currentColor" stroke="none" />
      <circle cx="62" cy="43" r="2.6" fill="currentColor" stroke="none" />
    </svg>
  );
}

const BY_KEY: Record<string, React.ComponentType<{ size: number }>> = {
  ai: AiArt,
  garment: GarmentArt,
  pos: PosArt,
  whatsapp: WhatsappArt,
};

export function AddonIllustration({ addonKey, size = 84 }: AddonIllustrationProps) {
  const Art = BY_KEY[addonKey] ?? AiArt;
  return <Art size={size} />;
}
